import { useState, useRef } from "react";
import { type ChatMessage } from "@workspace/api-client-react";
import { API_BASE } from "@/lib/api";

export function useChatStream(sessionId: number, initialMessages: ChatMessage[] = []) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isStreaming) return;

    const token = localStorage.getItem("studyflow_token");
    
    // Add user message to UI immediately
    const userMsg: ChatMessage = {
      id: Date.now(),
      sessionId,
      role: "user",
      message: content,
      createdAt: new Date().toISOString(),
    };
    
    // Add an empty assistant message to stream into
    const assistantMsg: ChatMessage = {
      id: Date.now() + 1,
      sessionId,
      role: "assistant",
      message: "",
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);
    setError(null);

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(`${API_BASE}/api/sessions/${sessionId}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: content }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        if (response.status === 403) {
          const errorBody = await response.json();
          throw new Error(errorBody.message || "You've reached your plan limit. Please upgrade.");
        }
        throw new Error("Failed to send message");
      }
      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let fullAssistantText = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n").filter((line) => line.trim().startsWith("data: "));
          
          for (const line of lines) {
            const dataStr = line.replace("data: ", "").trim();
            if (!dataStr) continue;
            
            try {
              const data = JSON.parse(dataStr);
              if (data.done) {
                done = true;
              } else if (data.content) {
                fullAssistantText += data.content;
                setMessages((prev) => {
                  const newMsgs = [...prev];
                  const last = newMsgs[newMsgs.length - 1];
                  if (last && last.role === "assistant") {
                    last.message = fullAssistantText;
                  }
                  return newMsgs;
                });
              }
            } catch (e) {
              console.error("Failed to parse SSE chunk", dataStr);
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || "An error occurred during chat");
        setMessages((prev) => prev.slice(0, -2));
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  return {
    messages,
    isStreaming,
    sendMessage,
    stopStreaming,
    error,
    setMessages,
  };
}
